import { influencer } from '@round/api';
import { getTableMetaHelper } from '@round/ui-kit';
import { useCreatorExplore } from '../../hooks/useCreatorExplore';
import type { ColumnDef, CellContext as LibCellContext } from '@tanstack/react-table';
import { tableHelpers } from '@round/creatorbase-ui';
import type { VisibilityState } from '@tanstack/react-table';
import { uniq } from 'lodash';
import { formatDate } from 'helpers';
import { getAverageDraftTimeLabel, getLastCollabLabel } from '../../utils/creatorSearchUtils';

export const PAGE_SIZE = 25;

/** Matches the API's `first_seen` sort field, so the column id doubles as the `order_by_*` param value. */
export const DATE_ADDED_COLUMN_ID = 'first_seen';

/** The backend guarantees `first_seen` is always set. */
export const getDateAddedValue = (row: Partial<Pick<influencer.CreatorSearchResult, 'first_seen'>>): string =>
    row.first_seen ? formatDate(row.first_seen) : '-';

/** Only shown while a blurb search is running, outside the presets. */
export const MATCH_SCORE_COLUMN_ID = 'blurb_match_score';

export const getMatchScoreValue = (row: Partial<Pick<influencer.CreatorSearchResult, 'blurb_match_score'>>): string =>
    typeof row.blurb_match_score === 'number' ? `${Math.round(row.blurb_match_score * 100)}%` : '-';

export const getDraftTimeValue = (row: Partial<Pick<influencer.CreatorSearchResult, 'metrics'>>): string =>
    getAverageDraftTimeLabel(row.metrics?.avg_draft_time_in_hours);

export const getLastCollabValue = (row: Partial<Pick<influencer.CreatorSearchResult, 'post_metrics'>>): string =>
    getLastCollabLabel(row.post_metrics?.most_recent_post_date);

export type ExplorePlatform = 'tiktok' | 'instagram';

export const EXPLORE_PLATFORMS: ExplorePlatform[] = ['tiktok', 'instagram'];

/** The platforms a creator has an account on, in the order the table lists them. */
export const getConnectedPlatforms = (creator: influencer.CreatorSearchResult) =>
    EXPLORE_PLATFORMS.filter((platform) => !!creator.platforms?.[platform]?.length);

/** The account the creator details modal opens on: TikTok first, then Instagram. `null` for a creator with neither. */
export const getCreatorDetailsAccount = (creator: influencer.CreatorSearchResult) => {
    const platform = getConnectedPlatforms(creator)[0];
    return platform ? { platform, account: creator.platforms[platform][0] } : null;
};

export type Meta = {
    isLoading: boolean;
    hasError?: boolean;
    getCreatorImages: ReturnType<typeof useCreatorExplore>['getCreatorImages'];
    getAreCreatorImagesLoading: (creator: influencer.CreatorSearchResult) => boolean;
    getRowSelectedPlatform: (creator: influencer.CreatorSearchResult) => ExplorePlatform;
    onRowPlatformSelect: (creatorId: string, platform: ExplorePlatform) => void;
    /** Omitted by the tables that share these cells but have no creator details modal to open. */
    onOpenCreatorDetails?: (creator: influencer.CreatorSearchResult) => void;
};

//TODO: add a getTableMeta helper in creatorbase-ui that considers the base meta type extension
export const getMeta = getTableMetaHelper<Meta>();

export type CellContext = LibCellContext<tableHelpers.WithLoadingRow<influencer.CreatorSearchResult>, unknown>;

export type Column = ColumnDef<tableHelpers.WithLoadingRow<influencer.CreatorSearchResult>, unknown>;

export const getCreatorName = (creator: influencer.CreatorSearchResult) => {
    return (
        creator.full_name ||
        creator.platforms?.tiktok[0]?.platform_username ||
        creator.platforms?.instagram[0]?.platform_username ||
        '-'
    );
};

const PRESET_IDS = ['general', 'performance', 'platform-overview'] as const;
export type PresetId = (typeof PRESET_IDS)[number];

export const VISIBILITY_PRESETS: { id: PresetId; name: string }[] = [
    { id: 'general', name: 'General' },
    { id: 'performance', name: 'Performance' },
    { id: 'platform-overview', name: 'Platform Overview' },
];
export const DEFAULT_PRESET_ID = PRESET_IDS[0];

export const PRESET_COLUMN_IDS: Record<PresetId, string[]> = {
    general: [
        'location',
        'top_audience_countries',
        'tags',
        'metrics.avg_draft_time_in_hours',
        'metrics.response_rate',
        'post_metrics.most_recent_post_date',
        'platform',
        'total_follower_count',
        'cost',
        'avg_video_views_per_post',
        'estimated_cpm',
        'avg_engagement_rate',
    ],
    performance: [
        'metrics.response_rate',
        'platform',
        'estimated_cpe',
        'total_follower_count',
        'avg_video_views_per_post',
        'estimated_cpm',
        'avg_engagement_rate',
    ],
    'platform-overview': [
        'location',
        'top_audience_countries',
        'tags',
        'platform',
        'total_follower_count',
        'cost',
        'avg_video_views_per_post',
        'estimated_cpm',
        'avg_engagement_rate',
    ],
};

const HIDEABLE_COLUMN_IDS = uniq(Object.values(PRESET_COLUMN_IDS).flat());

/** Columns outside every preset are always visible, so a preset switch can never hide them. */
export const isHideableColumn = (columnId: string) => HIDEABLE_COLUMN_IDS.includes(columnId);

export function getPresetVisibilityStateFromColumns(presetId: string): VisibilityState {
    const state: VisibilityState = {};
    if (!PRESET_IDS.includes(presetId as PresetId)) {
        for (const id of HIDEABLE_COLUMN_IDS) {
            state[id] = PRESET_COLUMN_IDS[DEFAULT_PRESET_ID].includes(id);
        }
        return state;
    }

    for (const id of HIDEABLE_COLUMN_IDS) {
        state[id] = PRESET_COLUMN_IDS[presetId as PresetId].includes(id);
    }
    return state;
}
